import { useState } from 'react';
import toast from 'react-hot-toast';

import Modal from './Modal';
import Button from './Button';
import { ReactComponent as GithubIcon } from '../assets/images/github-logo.svg';
import { ReactComponent as StatusErrorSVG } from '../assets/images/status-error.svg';
import { openGithubPermissions } from '../utils/githubPopup';
import back4app2 from '../back4app2';
import { AmplitudeEvent, trackEvent } from '../utils/amplitude';

/**
 * Offered when a deploy failed because the GitHub App installation is gone.
 *
 * This is the one deployment failure the customer can fix themselves in about
 * ten seconds, and until now the only trace of it was a line in the log panel
 * — `github app installation is not valid anymore`, under `deployment failed`,
 * with nothing on screen saying that re-granting access is the fix or where to
 * do it. People opened support tickets for it.
 *
 * It redeploys on its own afterwards, and that is the point: someone who has
 * just re-granted access wants the build that failed, not a second decision.
 *
 * But only once access is CONFIRMED. The window closing means the window
 * closed — the person may have granted access or changed their mind and hit
 * the X, and those are indistinguishable from here. So on close it asks GitHub
 * for the repository list and looks for this app's repository in it: that is
 * the exact capability the deploy needs, so finding it there is proof, and not
 * finding it is proof of the opposite.
 *
 * The alternative — retry blindly and let it fail — would have burned a
 * deployment to ask a question this answers for free, and left the customer
 * reading a second identical failure.
 */
const GitHubReconnectModal = ({ open, onClose, serviceEnvironmentId, repositoryId, repositoryFullName }: {
  open: boolean;
  onClose: () => void;
  serviceEnvironmentId?: string;
  /** What the access probe runs against. Without it the modal cannot verify
   * and falls back to retrying blind. */
  repositoryId?: string;
  repositoryFullName?: string;
}) => {
  const [isReconnecting, setIsReconnecting] = useState(false);

  const [notGranted, setNotGranted] = useState(false);

  const onReconnect = () => {
    trackEvent(AmplitudeEvent.EDIT_GITHUB_PERMISSION_CLICK);
    setIsReconnecting(true);
    setNotGranted(false);

    openGithubPermissions({
      onClosed: async () => {
        try {
          // Asking for the branches, because that is the ONE call that
          // reproduces what the deploy does: the resolver claims an
          // installation access token and hits GitHub with it, which is
          // exactly where the deploy died. If it answers, a retry will get
          // past the step that failed.
          //
          // The obvious check — "is the repository still in the list?" — was
          // tried first and verifies nothing: `gitHubRepositories` reads our
          // OWN database, so a repository whose access was revoked minutes ago
          // is still listed as accessible. It said yes, the deploy fired, and
          // it failed exactly as before.
          if (!repositoryId) {
            // Nothing to probe with. Retry and let the deploy be the check —
            // worse than asking, better than leaving the customer stuck.
            if (serviceEnvironmentId) {
              await back4app2.triggerManualDeployment(serviceEnvironmentId);
            }

            onClose();
            return;
          }

          try {
            await back4app2.repositoryBranches(repositoryId);
          } catch (accessError) {
            setNotGranted(true);
            return;
          }

          if (serviceEnvironmentId) {
            await back4app2.triggerManualDeployment(serviceEnvironmentId);
          }

          onClose();
        } catch (e) {
          // The check itself failed — a network blip, or the session expired
          // while the popup was open. Not the same as "access was not
          // granted", so it does not claim that.
          toast.error(
            'We could not check the GitHub access. Please try again.',
            {
              className:
                'bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] min-w-xs text-sm',
              icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
              duration: 6000,
            }
          );
        } finally {
          setIsReconnecting(false);
        }
      },
    });
  };

  return (
    // The house pattern for a decision modal — the light panel, centred icon,
    // narrow measure and centred buttons that Settings and the build dialogs
    // already use. It was built dark and wide first, which put a warm grey
    // panel on a navy page and ran the sentence to ~90 characters; neither was
    // a choice, just a default nobody had compared against the others.
    <Modal open={open} onClose={onClose}>
      <>
        <div className="flex w-full justify-center">
          <GithubIcon width="36px" height="36px" className="text-dark" />
        </div>

        <div className="font-semibold leading-140 text-dark text-center mt-3 mb-2">
          Back4app lost access to this repository
        </div>

        {/* On its own line, in mono. Inline in the sentence a long repository
            name broke mid-word across two lines and buried the one detail the
            customer needs to recognise which app this is about. */}
        {repositoryFullName && (
          <div className="text-center text-[0.8125rem] font-courrier text-dark-grey break-all mb-2">
            {repositoryFullName}
          </div>
        )}

        {/* The message changes once we have looked. Saying "grant access
            again" to someone who just came back from doing exactly that,
            without acknowledging that we checked and did not find it, is the
            kind of loop people give up on. */}
        {notGranted ? (
          <div className="text-sm text-center max-w-xs mx-auto mb-2">
            We still cannot see this repository. On the GitHub page, make sure
            it is included in the repositories Back4app can access — then try
            again.
          </div>
        ) : (
          <div className="text-sm text-center max-w-xs mx-auto mb-2">
            The GitHub App installation was removed or its access changed. Grant
            access again and we will retry the deployment for you.
          </div>
        )}

        <div className="flex justify-center space-x-2 w-full mt-4">
          <Button
            className="text-dark-grey border border-light-grey rounded-[5px]"
            onClick={onClose}
            isDisabled={isReconnecting}
          >
            Not now
          </Button>
          <Button onClick={onReconnect} type="primary" isDisabled={isReconnecting}>
            {isReconnecting
              ? 'Checking access…'
              : notGranted
                ? 'Open GitHub again'
                : 'Grant access and retry'}
          </Button>
        </div>
      </>
    </Modal>
  );
};

export default GitHubReconnectModal;
